import { useEffect, useState } from 'react';

import {
  Button,
  Loader,
  PasswordInput,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useForm } from '@mantine/form';
import { handleSubmitError } from '@utils/forms';

import {
  useActivateUserMutation,
  useVerifyUserActivationTokenQuery,
} from '@api/users.api';

interface ActivateUserProps {
  token: string;
  onSuccess?: () => void;
}

export default function ActivateUser({ token, onSuccess }: ActivateUserProps) {
  // ==========================================================================
  // State
  // ==========================================================================
  const [verified, setVerified] = useState(false);
  const { t } = useTranslation();

  // ==========================================================================
  // Api
  // ==========================================================================
  const {
    data: verifyTokenData,
    isLoading,
    error,
  } = useVerifyUserActivationTokenQuery(token);
  const [activateUser, { isLoading: isLoadingActivate }] =
    useActivateUserMutation();

  // ==========================================================================
  // Form
  // ==========================================================================
  const initialValues = {
    password: '',
    passwordConfirm: '',
  };

  const form = useForm({
    initialValues,
    validate: {
      passwordConfirm: (value, values) =>
        value !== values.password
          ? t('auth.activateAccount.validation.passwordMismatch')
          : null,
    },
  });

  // ==========================================================================
  // Handlers
  // ==========================================================================
  const onSubmit = async (values: typeof initialValues) => {
    try {
      const data = {
        password: values.password,
      };

      await activateUser({ token, body: data }).unwrap();

      setVerified(true);
      onSuccess?.();
    } catch (e) {
      handleSubmitError(e, form);
    }
  };

  useEffect(() => {
    (async () => {
      if (!verifyTokenData?.requiresPassword) {
        try {
          await activateUser({ token, body: {} }).unwrap();

          setVerified(true);
          onSuccess?.();
        } catch (e) {
          handleSubmitError(e, form);
        }
      }
    })();
  }, [verifyTokenData]);

  // ==========================================================================
  // Render
  // ==========================================================================
  return (
    <>
      {isLoading ? (
        <Loader />
      ) : error ? (
        <>
          <Text ta="center" fw="bold">
            {t('auth.activateAccount.invalidToken')}
          </Text>
          {/* TODO */}
          {/* <Button component={Link} to="/">
            Richiedi nuova verifica
          </Button> */}
        </>
      ) : verified ? (
        <>
          <Text ta="center" fw="bold">
            {t('auth.activateAccount.verified')}
          </Text>
        </>
      ) : (
        <Stack>
          <Title order={2} ta="center">
            {t('auth.activateAccount.title')}
          </Title>
          <Text ta="center" size="sm" mb="lg">
            {t('auth.activateAccount.instructions')}
          </Text>
          <form
            onSubmit={form.onSubmit((values) => {
              onSubmit(values);
            })}
          >
            <PasswordInput
              label={t('auth.activateAccount.password.label')}
              placeholder={t('auth.activateAccount.password.placeholder')}
              required
              {...form.getInputProps('password')}
            />
            <PasswordInput
              label={t('auth.activateAccount.passwordConfirm.label')}
              placeholder={t(
                'auth.activateAccount.passwordConfirm.placeholder',
              )}
              required
              mt="md"
              {...form.getInputProps('passwordConfirm')}
            />

            {form.errors.general && (
              <Text c="red" size="sm" mt="xl">
                {form.errors.general}
              </Text>
            )}
            <Button type="submit" fullWidth mt="xl" loading={isLoadingActivate}>
              {t('auth.activateAccount.submit')}
            </Button>
          </form>
        </Stack>
      )}
    </>
  );
}
